import {
	CheckCircle,
	CurrencyCircleDollar,
	Eye,
	XCircle,
	CaretDown,
} from '@phosphor-icons/react';
import Text from '../../../components/atoms/Text';
import {
	BasedDeductibles,
	Deductions,
	NotDeduction,
	TotalExpense,
} from '../../../@types/Declarations.type';
import { useState } from 'react';
import InvoiceDetail from './InvoiceDetail';
import Modal from '../../../components/molecules/Modal/Modal';
import { getFormatDate } from '../../../shared/utils/dates.util';
import { sendEvent } from '../../../services/user-interaction.service';
import {
	ANALYTICS_EVENTS,
	EVENT_TYPES,
} from '../../../shared/constants/events';
import useClarityEvent from '../../../hooks/useClarityEvent';
interface Step2Props {
	deductibles?: BasedDeductibles;
	expenses?: TotalExpense;
	viewApprove?: boolean;
	month: string;
	user_id?: number;
}

const Step2 = ({
	deductibles,
	expenses,
	viewApprove = true,
	month,
	user_id,
}: Step2Props) => {
	const [isOpen, setIsOpen] = useState(false);
	const [isOpenNoDeducibles, setIsOpenNoDeducibles] = useState(false);
	const [selectedInvoice, setSelectedInvoice] = useState<
		Deductions | NotDeduction
	>();
	const [isDeductibleInvoice, setIsDeductibleInvoice] =
		useState<boolean>(false);
	const [showDetailModal, setShowDetailModal] = useState(false);
	const { trackEvent } = useClarityEvent();
	const handleViewDetail = (deduction: Deductions | NotDeduction, isDeductible: boolean) => {
		setSelectedInvoice(deduction);
		setIsDeductibleInvoice(isDeductible);
		setShowDetailModal(true);
		viewDetailDeductions();
	};

	const viewDetailDeductions = async () => {
		if (!user_id) return;
		try {
			await sendEvent({
				user_id: user_id,
				event: ANALYTICS_EVENTS.APPROVAL_DECLARATIONS,
				source: 'web',
				event_payload: {
					subsection: EVENT_TYPES.APPROVAL_DECLARATIONS_VIEW_DETAIL_DEDUCTIONS,
				},
			});
			trackEvent(EVENT_TYPES.APPROVAL_DECLARATIONS_VIEW_DETAIL_DEDUCTIONS);
		} catch (error) {}
	};

	return (
		<>
			{viewApprove && (
				<>
					<div className='self-stretch inline-flex justify-start items-center gap-2.5'>
						<CurrencyCircleDollar
							weight='duotone'
							color='var(--h-primary)'
							size={16}
						/>
						<Text
							size='body-2'
							weight='medium'
						>
							Gastos y deducciones
						</Text>
					</div>

					<div className='self-stretch h-0 outline outline-1 outline-offset-[-0.50px] outline-[#e5e5e5]' />
				</>
			)}

			<div className='self-stretch flex-col inline-flex justify-start items-start gap-0'>
				<Text
					size='body-2'
					weight='regular'
				>
					Deducciones
				</Text>
				<Text
					size='body-3'
					weight='light'
				>
					Facturas registradas en {month} que podrían aplicar como deducciones
					mensuales.
				</Text>
			</div>

			{/* Dropdown de Deducibles */}
			<div className='w-full'>
				<button
					onClick={() => setIsOpen(!isOpen)}
					className='w-full flex items-center justify-between p-3 rounded-lg outline outline-1 outline-[#e5e5e5]'
				>
					<div className='flex items-center gap-2'>
						<div className='w-1.5 h-1.5 bg-h-success-50 rounded-full' />
						<Text
							size='caption'
							weight='regular'
						>
							Facturas deducibles:
						</Text>
						<Text
							size='caption'
							weight='light'
						>
							{deductibles?.deductions?.length || 0}
						</Text>
					</div>
					<CaretDown
						size={16}
						color='var(--h-primary)'
						className={`transform transition-transform ${isOpen ? 'rotate-180' : ''}`}
					/>
				</button>

				{isOpen && (
					<div className='mt-2'>
						{deductibles?.deductions?.map((deduction, index) => (
							<div
								key={`deductible-${index}`}
								className='rounded-lg p-2.5 mb-2 w-full outline outline-1 outline-[#e5e5e5]'
							>
								<div className='flex items-center justify-between'>
									<div className='flex items-center gap-2'>
										<div>
											<CheckCircle
												size={16}
												color='var(--h-success-50)'
												weight='duotone'
											/>
										</div>
										<div className='flex flex-col gap-[8px]'>
											<Text
												size='caption'
												weight='regular'
											>
												{deduction.isser_name}
											</Text>
											<Text
												size='mini-1'
												weight='light'
											>
												{getFormatDate(deduction.issued_at)}
											</Text>
										</div>
									</div>
									<div className='flex items-center gap-2'>
										<Text
											size='caption'
											weight='regular'
										>
											$
											{deduction.total === 0 && deduction.type === 'P'
												? Math.round(
														deduction.payment_amount || 0,
													).toLocaleString('es-MX')
												: Math.round(deduction.total).toLocaleString('es-MX')}
										</Text>
										<button
											title='Ver detalle'
											onClick={(e) => {
												e.stopPropagation();
												handleViewDetail(deduction, true);
											}}
											className='p-1 hover:bg-gray-100 rounded-full transition-colors'
										>
											<Eye
												size={16}
												color='var(--h-primary)'
												weight='duotone'
											/>
										</button>
									</div>
								</div>
							</div>
						))}
					</div>
				)}
			</div>

			{/* Dropdown de No Deducibles */}
			<div className='w-full'>
				<button
					onClick={() => setIsOpenNoDeducibles(!isOpenNoDeducibles)}
					className='w-full flex items-center justify-between p-3 rounded-lg outline outline-1 outline-[#e5e5e5]'
				>
					<div className='flex items-center gap-2'>
						<div className='w-1.5 h-1.5 bg-h-error-50 rounded-full' />
						<Text
							size='caption'
							weight='regular'
						>
							Facturas no deducibles:
						</Text>
						<Text
							size='caption'
							weight='light'
						>
							{deductibles?.not_deductions?.length || 0}
						</Text>
					</div>
					<CaretDown
						size={16}
						color='var(--h-primary)'
						className={`transform transition-transform ${isOpenNoDeducibles ? 'rotate-180' : ''}`}
					/>
				</button>

				{isOpenNoDeducibles && (
					<div className='mt-2'>
						{deductibles?.not_deductions?.map((deduction, index) => (
							<div
								key={`not-deductible-${index}`}
								className='rounded-lg p-2.5 mb-2 w-full outline outline-1 outline-[#e5e5e5]'
							>
								<div className='flex items-center justify-between'>
									<div className='flex items-center gap-2'>
										<div>
											<XCircle
												size={16}
												color='var(--h-error-50)'
												weight='duotone'
											/>
										</div>
										<div className='flex flex-col gap-[8px]'>
											<Text
												size='caption'
												weight='regular'
											>
												{deduction.isser_name}
											</Text>
											<Text
												size='mini-1'
												weight='light'
											>
												{getFormatDate(deduction.issued_at)}
											</Text>
										</div>
									</div>
									<div className='flex items-center gap-2'>
										<Text
											size='caption'
											weight='regular'
										>
											$
											{deduction.total === 0 && deduction.type === 'P'
												? Math.round(
														deduction.payment_amount || 0,
													).toLocaleString('es-MX')
												: Math.round(deduction.total).toLocaleString('es-MX')}
										</Text>
										<button
											title='Ver detalle'
											onClick={(e) => {
												e.stopPropagation();
												handleViewDetail(deduction, false);
											}}
											className='p-1 hover:bg-gray-100 rounded-full transition-colors'
										>
											<Eye
												size={16}
												color='var(--h-primary)'
												weight='duotone'
											/>
										</button>
									</div>
								</div>
							</div>
						))}
					</div>
				)}
			</div>

			<div className='flex justify-between items-center w-full'>
				<Text
					size='caption'
					weight='light'
				>
					Total en gastos:
				</Text>
				<Text
					size='caption'
					weight='medium'
				>
					$ {Math.round(expenses?.total || 0).toLocaleString('es-MX')}
				</Text>
			</div>
			<div className='flex justify-between items-center w-full'>
				<Text
					size='caption'
					weight='light'
				>
					Total IVA deducido:
				</Text>
				<Text
					size='caption'
					weight='medium'
				>
					${' '}
					{Math.round(deductibles?.totalDeductible || 0).toLocaleString(
						'es-MX',
					)}
				</Text>
			</div>

			<Modal
				open={showDetailModal}
				onClose={() => setShowDetailModal(false)}
				sx='!bg-transparent !p-0'
			>
				{selectedInvoice && (
					<InvoiceDetail
						detail={selectedInvoice as Deductions}
						isDeductible={isDeductibleInvoice}
					/>
				)}
			</Modal>
		</>
	);
};

export default Step2;
